import {
  ConnectionAcceptClientPacket,
  Ecf,
  Eif,
  Emf,
  Enf,
  EoReader,
  Esf,
  InitInitServerPacket,
  InitReply,
  InitSequenceStart,
  PacketAction,
  PacketFamily,
  serverVerificationHash,
} from 'eolib';
import type { Client } from '@/client';
import { saveEcf, saveEif, saveEmf, saveEnf, saveEsf } from '@/db';
import { DialogResourceID, EOResourceID } from '@/edf';
import { GameState } from '@/game-state';
import { SfxId } from '@/sfx';
import { ChatChannels, ChatIcon } from '@/ui/enums';

function handleInitInit(client: Client, reader: EoReader) {
  const packet = InitInitServerPacket.deserialize(reader);
  switch (packet.replyCode) {
    case InitReply.Ok:
      handleInitOk(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataOk,
      );
      break;
    case InitReply.OutOfDate:
      handleInitOutOfDate(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataOutOfDate,
      );
      break;
    case InitReply.PlayersList:
      handleInitPlayersList(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataPlayersList,
      );
      break;
    case InitReply.Banned:
      client.holdRejectedLogin();
      break;
    case InitReply.FileEcf:
      handleInitFileEcf(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataFileEcf,
      );
      break;
    case InitReply.FileEif:
      handleInitFileEif(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataFileEif,
      );
      break;
    case InitReply.FileEnf:
      handleInitFileEnf(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataFileEnf,
      );
      break;
    case InitReply.FileEsf:
      handleInitFileEsf(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataFileEsf,
      );
      break;
    case InitReply.FileEmf:
      handleInitFileEmf(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataFileEmf,
      );
      break;
    case InitReply.WarpMap:
      handleInitWarpMap(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataWarpMap,
      );
      break;
    case InitReply.MapMutation:
      handleInitMapMutation(
        client,
        packet.replyCodeData as InitInitServerPacket.ReplyCodeDataMapMutation,
      );
      break;
  }
}

function handleInitOk(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataOk,
) {
  if (data.challengeResponse !== serverVerificationHash(client.challenge)) {
    const text = client.getDialogStrings(
      DialogResourceID.CONNECTION_LOST_CONNECTION,
    );
    client.alertController.show(text[0], text[1]);
    client.disconnect();
    return;
  }

  client.playerId = data.playerId;
  // Hack to keep pre-game UI stable
  client.nearby.characters[0].playerId = data.playerId;
  client.bus.setEncryption(
    data.clientEncryptionMultiple,
    data.serverEncryptionMultiple,
  );
  client.bus.setSequence(
    InitSequenceStart.fromInitValues(data.seq1, data.seq2),
  );

  const packet = new ConnectionAcceptClientPacket();
  packet.clientEncryptionMultiple = data.clientEncryptionMultiple;
  packet.serverEncryptionMultiple = data.serverEncryptionMultiple;
  packet.playerId = data.playerId;
  client.bus.send(packet);
  client.setState(GameState.Connected);

  if (
    client.postConnectState === GameState.Login &&
    client.authenticationController.shouldAutoLogin()
  ) {
    client.authenticationController.autoLogin();
    return;
  }

  if (!client.postConnectState) {
    return;
  }

  client.setState(client.postConnectState);
  client.postConnectState = undefined;
}

function handleInitPlayersList(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataPlayersList,
) {
  client.socialController.notifyPlayersList(data.playersList.players);
}

function handleInitOutOfDate(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataOutOfDate,
) {
  const strings = client.getDialogStrings(
    DialogResourceID.CONNECTION_CLIENT_OUT_OF_DATE,
  );
  client.alertController.show(
    strings[0],
    `${strings[1]} ${data.version.major}.${data.version.minor}.${data.version.patch}`,
  );
  client.disconnect();
}

function handleInitFileEcf(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataFileEcf,
) {
  const reader = new EoReader(data.pubFile.content);
  client.ecf = Ecf.deserialize(reader);
  saveEcf(client.ecf);

  if (client.downloadQueue.length > 0) {
    const download = client.downloadQueue.pop();
    client.sessionController.requestFile(download!.type, download!.id);
  } else {
    client.sessionController.enterGame();
  }
}

function handleInitFileEif(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataFileEif,
) {
  const reader = new EoReader(data.pubFile.content);
  client.eif = Eif.deserialize(reader);
  saveEif(client.eif);

  if (client.downloadQueue.length > 0) {
    const download = client.downloadQueue.pop();
    client.sessionController.requestFile(download!.type, download!.id);
  } else {
    client.sessionController.enterGame();
  }
}

function handleInitFileEnf(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataFileEnf,
) {
  const reader = new EoReader(data.pubFile.content);
  client.enf = Enf.deserialize(reader);
  saveEnf(client.enf);

  if (client.downloadQueue.length > 0) {
    const download = client.downloadQueue.pop();
    client.sessionController.requestFile(download!.type, download!.id);
  } else {
    client.sessionController.enterGame();
  }
}

function handleInitFileEsf(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataFileEsf,
) {
  const reader = new EoReader(data.pubFile.content);
  client.esf = Esf.deserialize(reader);
  saveEsf(client.esf);

  if (client.downloadQueue.length > 0) {
    const download = client.downloadQueue.pop();
    client.sessionController.requestFile(download!.type, download!.id);
  } else {
    client.sessionController.enterGame();
  }
}

function handleInitFileEmf(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataFileEmf,
) {
  const reader = new EoReader(data.mapFile.content);
  client.setMap(Emf.deserialize(reader));
  saveEmf(client.mapId, client.map!);

  if (client.downloadQueue.length > 0) {
    const download = client.downloadQueue.pop();
    client.sessionController.requestFile(download!.type, download!.id);
  } else {
    client.sessionController.enterGame();
  }
}

function handleInitWarpMap(client: Client, data: InitInitServerPacket.ReplyCodeDataWarpMap) {
  try { client.sessionController.receiveWarpMap(Emf.deserialize(new EoReader(data.mapFile.content))); }
  catch (error) { console.error('Invalid destination map received', error); }
}

function handleInitMapMutation(
  client: Client,
  data: InitInitServerPacket.ReplyCodeDataMapMutation,
) {
  const reader = new EoReader(data.mapFile.content);
  const map = Emf.deserialize(reader);
  saveEmf(client.warpMapId, map);
  client.setMap(map);
  client.refresh();
  client.audioController.playById(SfxId.MapMutation);
  const message = `${client.getResourceString(EOResourceID.STRING_SERVER)} ${client.getResourceString(EOResourceID.SERVER_MESSAGE_MAP_MUTATION)}}`;
  client.chatController.notifyChat({
    channel: ChatChannels.Local,
    icon: ChatIcon.Exclamation,
    message,
  });

  client.chatController.notifyChat({
    channel: ChatChannels.System,
    icon: ChatIcon.Exclamation,
    message,
  });
}

export function registerInitHandlers(client: Client) {
  client.bus!.registerPacketHandler(
    PacketFamily.Init,
    PacketAction.Init,
    (reader) => handleInitInit(client, reader),
  );
}
